import { Lodgement, YesNoList, titleList } from 'contexts/FormProvider';
import * as yup from 'yup';
import { phoneNumberSchema, emailSchema } from 'utils/utils';

import countries from 'resources/addresses';
import { isEmpty } from 'lodash';
import { textAreaRegex, validatePostCode, validateTextSchema } from 'utils/commonSchema';

const textFieldMin = 4;
const textFieldMax = 50;
const dependentFieldMax = 100;
const textFieldNameMin = 1;
const otherLanguageMin = 3;
const addressMax = 225;
const stateMax = 20;

const postalAddressErrorMsgMin = 'Remember to include your unit number as well as your street number';

export const dependentNameSchema = yup
  .string()
  .min(textFieldNameMin)
  .max(dependentFieldMax)
  .trim()
  .matches(textAreaRegex)
  .required();

const ageErrorMessage = 'Please enter a numeric value';

export const dependentAgeSchema = yup
  .string()
  .matches(/^[\d]{1,3}$/, ageErrorMessage)
  .required(ageErrorMessage);

export const dependentSchema = yup.array().of(
  yup.object().shape(
    {
      dependentName: yup.string().when('dependentAge', {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        is: (dependentAge: any) => !isEmpty(dependentAge),
        then: () => dependentNameSchema,
      }),
      dependentAge: yup.string().when('dependentName', {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        is: (dependentName: any) => !isEmpty(dependentName),
        then: () => dependentAgeSchema,
        otherwise: () => yup.string().nullable(),
      }),
    },
    [
      ['dependentName', 'dependentAge'],
      ['dependentAge', 'dependentName'],
    ]
  )
);
export const personalDetailsSchemaObject = {
  policyOrClaim: yup.string().when('lodgement', {
    is: 'Financial hardship application',
    then: () => yup.string().oneOf(['claim']).required(),
  }),

  claimNumber: yup.string().when('policyOrClaim', {
    is: 'claim',
    then: () =>
      yup
        .string()
        .matches(textAreaRegex, 'Please enter alpha numeric characters only')
        .min(textFieldMin, 'Please enter 4 characters or more')
        .max(textFieldMax)
        .required('Please enter 4 characters or more'),
  }),

  title: yup
    .string()
    .nullable()
    .oneOf([...titleList, null])
    .optional(),

  firstname: validateTextSchema({ minLen: textFieldNameMin, maxLen: textFieldMax, required: true }),
  lastname: validateTextSchema({ minLen: textFieldNameMin, maxLen: textFieldMax, required: true }),

  isLangInterpreter: yup.string().when('lodgement', {
    is: 'Financial hardship application',
    then: () =>
      yup
        .string()
        .nullable()
        .oneOf([...YesNoList])
        .required(),
  }),
  language: yup
    .string()
    .nullable()
    .when('isLangInterpreter', {
      is: 'yes',
      then: () =>
        yup
          .string()
          .oneOf([
            'Arabic',
            'Cantonese',
            'Greek',
            'Hindi',
            'Italian',
            'Mandarin',
            'Punjabi',
            'Spanish',
            'Vietnamese',
            'Other',
          ])
          .required(),
    }),

  otherLanguage: yup
    .string()
    .nullable()
    .when(['isLangInterpreter', 'language'], {
      is: (isLangInterpreter: string, language: string) => isLangInterpreter === 'yes' && language === 'Other',
      then: () => validateTextSchema({ minLen: otherLanguageMin, maxLen: textFieldMax, required: true }),
    }),

  dependents: dependentSchema,
};

const contactDetailSchemaObject = {
  iContactPreferedMethod: yup.string().oneOf(['email', 'post', 'phone']).required(),

  addressType: yup.string().when(['iContactPreferedMethod'], {
    is: 'post',
    then: () =>
      yup
        .string()
        .oneOf([...YesNoList])
        .nullable()
        .required(),
    otherwise: () => yup.string().nullable(),
  }),

  cEmailAddress: yup.string().when('iContactPreferedMethod', {
    is: 'email',
    then: () => emailSchema({ excludeEmptyString: false }),
    otherwise: () => emailSchema({ excludeEmptyString: true, required: false }),
  }),

  physicalAddress: yup
    .string()
    .when(['lodgement', 'isManualPhysAddress'], {
      is: (lodgement: Lodgement, isManualPhysAddress: boolean) =>
        lodgement === 'Financial hardship application' && !isManualPhysAddress,
      then: () => yup.string().required(postalAddressErrorMsgMin).min(4, postalAddressErrorMsgMin),
    })
    .when(['isManualPhysAddress', 'lodgement'], {
      is: (isManualPhysAddress: boolean, lodgement: Lodgement) =>
        !isManualPhysAddress && lodgement !== 'Financial hardship application',
      then: () => yup.string().nullable(),
    }),

  isPhysAddressValid: yup
    .boolean()
    .when(['lodgement', 'isManualPhysAddress'], {
      is: (lodgement: Lodgement, isManualPhysAddress: boolean) =>
        lodgement === 'Financial hardship application' && !isManualPhysAddress,
      then: () => yup.boolean().required().oneOf([true]),
    })
    .when(['isManualPhysAddress', 'lodgement'], {
      is: (isManualPhysAddress: boolean, lodgement: Lodgement) =>
        !isManualPhysAddress && lodgement !== 'Financial hardship application',
      then: () => yup.boolean(),
    }),

  phyCountry: yup
    .string()
    .nullable()
    .when('isManualPhysAddress', {
      is: true,
      then: () => yup.string().required().oneOf(countries, 'Country is a required field').label('Country'),
    }),

  phyState: yup.string().when('isManualPhysAddress', {
    is: true,
    then: () => validateTextSchema({ minLen: textFieldNameMin, maxLen: stateMax, required: true }),
  }),

  phyStreetAddress: yup.string().when('isManualPhysAddress', {
    is: true,
    then: () => validateTextSchema({ minLen: textFieldNameMin, maxLen: addressMax, required: true }),
  }),

  phySuburb: yup.string().when('isManualPhysAddress', {
    is: true,
    then: () => validateTextSchema({ minLen: textFieldNameMin, maxLen: addressMax, required: true }),
  }),

  phyPostcode: yup.string().test('postalCode', 'Invalid postcode', function (postCode) {
    const { phyCountry, phyState, isManualPhysAddress } = this.parent;
    if (isManualPhysAddress && typeof postCode === 'string') {
      return validatePostCode(phyCountry)(phyState)(postCode);
    }
    return true;
  }),

  postalAddress: yup
    .string()
    .nullable()
    .when(['lodgement', 'iContactPreferedMethod', 'isManualPosAddress', 'addressType'], {
      is: (lodgement: Lodgement, iContactPreferedMethod: string, isManualPosAddress: boolean, addressType: string) =>
        lodgement === 'Financial hardship application' &&
        iContactPreferedMethod === 'post' &&
        !isManualPosAddress &&
        addressType === 'no',
      then: () => yup.string().required(postalAddressErrorMsgMin).min(4, postalAddressErrorMsgMin),
    })
    .when(['isManualPosAddress', 'iContactPreferedMethod', 'lodgement'], {
      is: (isManualPosAddress: boolean, iContactPreferedMethod: string, lodgement: Lodgement) =>
        !isManualPosAddress && iContactPreferedMethod === 'post' && lodgement !== 'Financial hardship application',
      then: () => yup.string().nullable(),
    }),

  isPosAddressValid: yup.boolean().when(['isManualPosAddress', 'addressType'], {
    is: (isManualPosAddress: boolean, addressType: string) => !isManualPosAddress && addressType === 'no',
    then: () => yup.boolean().required().oneOf([true]),
  }),

  posStreetAddress: yup.string().when(['isManualPosAddress', 'addressType'], {
    is: (isManualPosAddress: boolean, addressType: string) => isManualPosAddress && addressType === 'no',
    then: () => validateTextSchema({ minLen: textFieldNameMin, maxLen: addressMax, required: true }),
  }),

  posSuburb: yup.string().when(['isManualPosAddress', 'addressType'], {
    is: (isManualPosAddress: boolean, addressType: string) => isManualPosAddress && addressType === 'no',
    then: () => validateTextSchema({ minLen: textFieldNameMin, maxLen: addressMax, required: true }),
  }),

  posState: yup.string().when(['isManualPosAddress', 'addressType'], {
    is: (isManualPosAddress: boolean, addressType: string) => isManualPosAddress && addressType === 'no',
    then: () => validateTextSchema({ minLen: textFieldNameMin, maxLen: stateMax, required: true }),
  }),

  posCountry: yup
    .string()
    .nullable()
    .when(['isManualPosAddress', 'addressType'], {
      is: (isManualPosAddress: boolean, addressType: string) => isManualPosAddress && addressType === 'no',
      then: () => yup.string().oneOf(countries, 'Country is a required field').required().label('Country'),
    }),

  posPostcode: yup.string().test('postalCode', 'Invalid postcode', function (postCode) {
    const { posCountry, isManualPosAddress, posState, addressType } = this.parent;

    if (isManualPosAddress && addressType === 'no' && typeof postCode === 'string') {
      return validatePostCode(posCountry)(posState)(postCode);
    }

    return true;
  }),

  haveOneValidNumber: yup.boolean().when('iContactPreferedMethod', {
    is: 'phone',
    then: () => yup.boolean().oneOf([true]),
    otherwise: () => yup.boolean(),
  }),

  mPhoneNumber: phoneNumberSchema(),
  hPhoneNumber: phoneNumberSchema(),
  oPhoneNumber: phoneNumberSchema(),

  nominatedRepType: yup
    .string()
    .oneOf([...YesNoList])
    .nullable()
    .required(),

  repFirstName: yup.string().when('nominatedRepType', {
    is: 'yes',
    then: () => validateTextSchema({ minLen: textFieldNameMin, maxLen: textFieldMax, required: true }),
    otherwise: () => yup.string().nullable(),
  }),

  repLastName: yup.string().when('nominatedRepType', {
    is: 'yes',
    then: () => validateTextSchema({ minLen: textFieldNameMin, maxLen: textFieldMax, required: true }),
    otherwise: () => yup.string().nullable(),
  }),

  repContactMethod: yup.string().when('nominatedRepType', {
    is: 'yes',
    then: () => yup.string().oneOf(['email', 'post', 'phone']).required(),
    otherwise: () => yup.string(),
  }),

  repEmailAddress: yup.string().when(['nominatedRepType', 'repContactMethod'], {
    is: (nominatedRepType: string, repContactMethod: string) =>
      nominatedRepType === 'yes' && repContactMethod === 'email',
    then: () => emailSchema({ excludeEmptyString: false }),
    otherwise: () => emailSchema({ excludeEmptyString: true, required: false }),
  }),

  repPostalAddress: yup.string().when(['repContactMethod', 'nominatedRepType', 'isManualRepPosAddress'], {
    is: (repContactMethod: string, nominatedRepType: string, isManualRepPosAddress: boolean) =>
      repContactMethod === 'post' && !isManualRepPosAddress && nominatedRepType === 'yes',
    then: () => yup.string().required(postalAddressErrorMsgMin).min(4, postalAddressErrorMsgMin),
    otherwise: () => yup.string().nullable(),
  }),

  isRepPosAddressValid: yup.boolean().when(['repContactMethod', 'isManualRepPosAddress', 'nominatedRepType'], {
    is: (repContactMethod: string, isManualRepPosAddress: boolean, nominatedRepType: string) =>
      repContactMethod === 'post' && nominatedRepType === 'yes' && !isManualRepPosAddress,
    then: () => yup.boolean().required().oneOf([true]),
    otherwise: () => yup.boolean(),
  }),

  repPosCountry: yup
    .string()
    .nullable()
    .when(['repContactMethod', 'isManualRepPosAddress', 'nominatedRepType'], {
      is: (repContactMethod: string, isManualRepPosAddress: boolean, nominatedRepType: string) =>
        repContactMethod === 'post' && nominatedRepType === 'yes' && isManualRepPosAddress,
      then: () => yup.string().required().oneOf(countries, 'Country is a required field').label('Country'),
    }),

  repPosState: yup.string().when(['repContactMethod', 'isManualRepPosAddress', 'nominatedRepType'], {
    is: (repContactMethod: string, isManualRepPosAddress: boolean, nominatedRepType: string) =>
      repContactMethod === 'post' && nominatedRepType === 'yes' && isManualRepPosAddress,
    then: () => validateTextSchema({ minLen: textFieldNameMin, maxLen: stateMax, required: true }),
  }),

  repPosStreetAddress: yup.string().when(['repContactMethod', 'isManualRepPosAddress', 'nominatedRepType'], {
    is: (repContactMethod: string, isManualRepPosAddress: boolean, nominatedRepType: string) =>
      repContactMethod === 'post' && nominatedRepType === 'yes' && isManualRepPosAddress,
    then: () => validateTextSchema({ minLen: textFieldNameMin, maxLen: addressMax, required: true }),
  }),

  repPosSuburb: yup.string().when(['repContactMethod', 'isManualRepPosAddress', 'nominatedRepType'], {
    is: (repContactMethod: string, isManualRepPosAddress: boolean, nominatedRepType: string) =>
      repContactMethod === 'post' && nominatedRepType === 'yes' && isManualRepPosAddress,
    then: () => validateTextSchema({ minLen: textFieldNameMin, maxLen: addressMax, required: true }),
  }),

  repPosPostcode: yup.string().test('postalCode', 'Invalid postcode', function (postCode) {
    const { repPosCountry, repPosState, isManualRepPosAddress, repContactMethod, nominatedRepType } = this.parent;
    if (
      repContactMethod === 'post' &&
      nominatedRepType === 'yes' &&
      isManualRepPosAddress &&
      typeof postCode === 'string'
    ) {
      return validatePostCode(repPosCountry)(repPosState)(postCode);
    }
    return true;
  }),

  haveOneValidRepNumber: yup.boolean().when(['nominatedRepType', 'repContactMethod'], {
    is: (nominatedRepType: string, repContactMethod: string) =>
      nominatedRepType === 'yes' && repContactMethod === 'phone',
    then: () => yup.boolean().oneOf([true]),
    otherwise: () => yup.boolean(),
  }),

  repMPhoneNumber: phoneNumberSchema(),
  repHPhoneNumber: phoneNumberSchema(),
  repOPhoneNumber: phoneNumberSchema(),
};

export const personalDetailsSchema = yup.object().shape(personalDetailsSchemaObject);
export const contactDetailSchema = yup.object().shape(contactDetailSchemaObject);

const validationSchema = yup.object({
  ...personalDetailsSchemaObject,
  ...contactDetailSchemaObject,
});

export default validationSchema;
